import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import createFuzzySearch from '@nozbe/microfuzz';
import { IResourcesFiltersTagCategory } from 'app/components/Resources/hooks/useResourcesFiltersService';
import { CloseIconSmall, SearchIconSmall, SimpleTagIcon } from 'app/components/shared/icons';
import { FormikProps, useFormikContext } from 'formik';
import { AnimatePresence } from 'framer-motion';
import useDebounce from 'hooks/useDebounce';
import { ThemeProvider } from 'styled-components';

import { useResourceAvailableTags } from '../hooks/useResourceAvailableTags';
import {
  ResourceSubmissionTagsFieldOverallWrapper,
  ResourceSubmissionTagsFieldSearchIconContainer,
  ResourceSubmissionTagsFieldSearchInput,
  ResourceSubmissionTagsFieldSearchInputContainer,
  ResourceSubmissionTagsFieldSearchSelectedTags,
  ResourceSubmissionTagsFieldSearchTagsDropdownItem,
  ResourceSubmissionTagsFieldSearchTagsDropdownItemAdd,
  ResourceSubmissionTagsFieldSearchTagsDropdownItemAlreadyAdded,
  ResourceSubmissionTagsFieldSearchTagsDropdownItemLabel,
  ResourceSubmissionTagsFieldSearchTagsDropdownWrapper,
  ResourceSubmissionTagsFieldSearchTagsPill,
  ResourceSubmissionTagsFieldSearchTagsPillIcon,
  ResourceSubmissionTagsFieldSearchTagsPillLabel,
  ResourceSubmissionTagsFieldSearchWrapper,
  ResourceSubmissionTagsFieldSubtitle,
} from './styled';

export interface ResourceSubmissionTagsFieldTag {
  uuid: string;
  name: string;
}

export interface ResourceSubmissionTagsFieldProps extends FormikProps<any> {
  name: string;
  required?: boolean;
}

export interface ResourceSubmissionTagsFieldTagCategory extends IResourcesFiltersTagCategory {
  name: string;
}

export default function ResourceSubmissionTagsField(props: ResourceSubmissionTagsFieldProps) {
  const formikContext = useFormikContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [list, setList] = useState<any[]>([]);
  const [currentValue, setCurrentValue] = useState<string>('');
  const debouncedCurrentValue = useDebounce(currentValue, 300);
  const { data, isLoading } = useResourceAvailableTags();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Backspace' &&
      currentValue.trim() === '' &&
      formikContext.values[props.name]?.length > 0
    ) {
      const lastTag = formikContext.values[props.name][formikContext.values[props.name].length - 1];
      handleTagClick(lastTag);
    }
  };

  const tags = useMemo<any[]>(() => {
    if (!data) return [];

    return data?.tags;
  }, [data]);

  const fuzzySearch = useMemo(() => {
    if (tags) {
      return createFuzzySearch(tags, { key: 'name' });
    }
  }, [tags]);

  const fuzzySearchCallback = useCallback(
    (value) => {
      if (fuzzySearch) {
        return fuzzySearch(value).map((result) => result.item);
      }
    },
    [debouncedCurrentValue, fuzzySearch]
  );

  useEffect(() => {
    if (fuzzySearch && debouncedCurrentValue.trim() !== '') {
      setList(fuzzySearchCallback(debouncedCurrentValue));
    } else {
      setList([]);
    }
  }, [debouncedCurrentValue, fuzzySearchCallback, tags]);

  const handleTagClick = (tag: string) => {
    const currentTags = formikContext.values[props.name] || [];
    const exists = currentTags.find((t) => t === tag);
    let finalTags = [];

    if (exists) {
      finalTags = currentTags.filter((t) => t !== tag);
    } else {
      finalTags = [...currentTags, tag];
    }

    formikContext.setFieldTouched(props.name, true);
    formikContext.setFieldValue(props.name, finalTags);
    setCurrentValue('');
    setList([]);
    inputRef.current.value = '';
  };

  const handleRemoveTag = (tag: string) => {
    const currentTags = formikContext.values[props.name] || [];
    const finalTags = currentTags.filter((t) => t !== tag);

    formikContext.setFieldTouched(props.name, true);
    formikContext.setFieldValue(props.name, finalTags);
  };

  const sortAlphabetically = (
    a: ResourceSubmissionTagsFieldTagCategory,
    b: ResourceSubmissionTagsFieldTagCategory
  ) => a.name.localeCompare(b.name);

  const handleChange = (e) => {
    if (isLoading) return;

    setCurrentValue(e.target.value);
  };

  const isError = useMemo(() => {
    return formikContext.errors[props.name] && formikContext.touched[props.name];
  }, [formikContext.errors, formikContext.touched, props.name]);

  const isTagSelected = useCallback(
    (tag) => {
      return formikContext.values[props.name]?.includes(tag);
    },
    [formikContext.values, props.name]
  );

  return (
    <ThemeProvider theme={{ isError }}>
      <ResourceSubmissionTagsFieldOverallWrapper>
        <input type="hidden" name={props.name} value={formikContext.values[props.name]} />
        {props.required !== undefined ? (
          props.required ? (
            <ResourceSubmissionTagsFieldSubtitle>
              {isError
                ? formikContext.errors[props.name]
                : 'Required. These are the broader topics that these materials fall into.'}
            </ResourceSubmissionTagsFieldSubtitle>
          ) : (
            <ResourceSubmissionTagsFieldSubtitle>
              {isError ? formikContext.errors[props.name] : 'Optional. Pick 1 - 3 relevant Topics'}
            </ResourceSubmissionTagsFieldSubtitle>
          )
        ) : null}
        <ResourceSubmissionTagsFieldSearchWrapper>
          <ResourceSubmissionTagsFieldSearchSelectedTags>
            {formikContext.values[props.name] &&
              formikContext.values[props.name]?.map((tag) => (
                <ResourceSubmissionTagsFieldSearchTagsPill>
                  <SimpleTagIcon width={16} height={16} />
                  <ResourceSubmissionTagsFieldSearchTagsPillLabel>
                    {tag}
                  </ResourceSubmissionTagsFieldSearchTagsPillLabel>
                  <ResourceSubmissionTagsFieldSearchTagsPillIcon
                    data-action="remove"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    <CloseIconSmall width={9} height={9} />
                  </ResourceSubmissionTagsFieldSearchTagsPillIcon>
                </ResourceSubmissionTagsFieldSearchTagsPill>
              ))}
            <ResourceSubmissionTagsFieldSearchInputContainer>
              <ResourceSubmissionTagsFieldSearchInput
                ref={inputRef}
                placeholder={
                  formikContext.values[props.name]?.length > 0
                    ? null
                    : 'Start typing to search for tags'
                }
                onChange={handleChange}
                onKeyDown={handleKeyDown}
              />
              <ResourceSubmissionTagsFieldSearchIconContainer>
                <SearchIconSmall width={24} height={24} fill="var(--color-primary)" />
              </ResourceSubmissionTagsFieldSearchIconContainer>
            </ResourceSubmissionTagsFieldSearchInputContainer>
          </ResourceSubmissionTagsFieldSearchSelectedTags>
          <AnimatePresence>
            {debouncedCurrentValue.trim() !== '' ? (
              <ResourceSubmissionTagsFieldSearchTagsDropdownWrapper>
                {!list
                  .map((listItem) => listItem.name.toLowerCase())
                  .includes(debouncedCurrentValue.toLowerCase()) ? (
                  <ResourceSubmissionTagsFieldSearchTagsDropdownItem
                    onClick={() => handleTagClick(debouncedCurrentValue)}
                  >
                    <ResourceSubmissionTagsFieldSearchTagsDropdownItemLabel>
                      {debouncedCurrentValue}
                    </ResourceSubmissionTagsFieldSearchTagsDropdownItemLabel>
                    <ResourceSubmissionTagsFieldSearchTagsDropdownItemAdd>
                      ADD NEW
                    </ResourceSubmissionTagsFieldSearchTagsDropdownItemAdd>
                  </ResourceSubmissionTagsFieldSearchTagsDropdownItem>
                ) : null}
                {list.map((tag) => (
                  <ResourceSubmissionTagsFieldSearchTagsDropdownItem
                    key={tag.name}
                    onClick={() => handleTagClick(tag.name)}
                  >
                    <ResourceSubmissionTagsFieldSearchTagsDropdownItemLabel
                      alreadyAdded={isTagSelected(tag.name)}
                    >
                      {tag.name}
                    </ResourceSubmissionTagsFieldSearchTagsDropdownItemLabel>
                    {isTagSelected(tag.name) ? (
                      <ResourceSubmissionTagsFieldSearchTagsDropdownItemAlreadyAdded>
                        ALREADY ADDED
                      </ResourceSubmissionTagsFieldSearchTagsDropdownItemAlreadyAdded>
                    ) : null}
                  </ResourceSubmissionTagsFieldSearchTagsDropdownItem>
                ))}
              </ResourceSubmissionTagsFieldSearchTagsDropdownWrapper>
            ) : null}
          </AnimatePresence>
        </ResourceSubmissionTagsFieldSearchWrapper>
      </ResourceSubmissionTagsFieldOverallWrapper>
    </ThemeProvider>
  );
}
